import { StorageError } from '../../app/errors';
import { requireTransaction } from '../commit';
import { db, TABLE } from '../db';
import { SINGLETON_ID } from '../../types';
import type { ActiveMatchRow, UiSessionRow } from '../../types';

/**
 * The two singleton rows behind resume (§2.5) and the single-writer advisory lock (§13.7).
 *
 * ── One row, two jobs, and that is deliberate ────────────────────────────────
 *
 * `ActiveMatch` carries **both** §2.5's resume pointer (`matchId`) and §13.7's lock
 * (`writerSessionId` + `heartbeatTs`). They are the same fact seen from two sides: "this browsing
 * context is the one capturing, and this is what it is capturing." Splitting them into two rows
 * would create a state where a context holds the lock but not the pointer, or the reverse, and
 * nothing would be able to say which was authoritative.
 *
 * `matchId` is `null` when no match is active — §2.5 makes the *pointer* present iff a match is in
 * `preMatch` or an in-match state, while the *lock* is held from the moment a context claims it. A
 * context that has claimed but started nothing is the normal state of the app at Home.
 *
 * ── The two lock fields are a DIAGNOSTIC, never enforcement ──────────────────
 *
 * §13.7 was re-decided onto the Web Locks API: the browser holds the lock and releases it when it
 * destroys the page. `writerSessionId` and `heartbeatTs` are retained only because
 * `navigator.locks.query()` carries neither — they say *who* wrote last and *when*, for OBS-1 and
 * for the detail line under U-9's banner.
 *
 * **Nothing anywhere may branch on their age.** The withdrawn mechanism compared `heartbeatTs`
 * against a clock and a 90-second stale window, and the result was that a relaunched PWA spent up
 * to 90 seconds degraded while blaming a window that did not exist. There is no staleness rule left
 * to put here or anywhere else.
 */

// ─────────────────────────────────────────────────────────────────────────────
// ActiveMatch — §2.5 resume pointer + §13.7 writer lock
// ─────────────────────────────────────────────────────────────────────────────

/** @throws {StorageError} on a read failure. */
export async function readActiveMatch(): Promise<ActiveMatchRow | undefined> {
  try {
    return await db().activeMatch.get(SINGLETON_ID);
  } catch (cause) {
    throw new StorageError('Could not read the active-match pointer.', {
      cause,
      context: { store: TABLE.activeMatch },
    });
  }
}

/**
 * Writes the singleton whole.
 *
 * A whole-row `put` rather than a partial patch, because the three fields are only ever meaningful
 * together: a heartbeat refresh that left a stale `writerSessionId` would advertise a live claim
 * for a context that no longer holds it.
 *
 * @throws {StorageError} outside a transaction, or on a write failure.
 */
export async function writeActiveMatch(row: Omit<ActiveMatchRow, 'id'>): Promise<void> {
  requireTransaction('session.writeActiveMatch');
  try {
    await db().activeMatch.put({ id: SINGLETON_ID, ...row });
  } catch (cause) {
    throw new StorageError('Could not update the active-match pointer.', {
      cause,
      context: { store: TABLE.activeMatch, matchId: row.matchId },
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// UiSession — §2.5 / §1.4
// ─────────────────────────────────────────────────────────────────────────────

/** @throws {StorageError} on a read failure. */
export async function readUiSession(): Promise<UiSessionRow | undefined> {
  try {
    return await db().uiSession.get(SINGLETON_ID);
  } catch (cause) {
    throw new StorageError('Could not read the saved screen.', {
      cause,
      context: { store: TABLE.uiSession },
    });
  }
}

/**
 * Records which surface the device is on.
 *
 * §2.5: written on surface **entry and exit only — never on the tap path**. That is a caller
 * obligation this function cannot enforce, and it is the reason design §1.4 keeps the record tiny:
 * a UI refactor must never become a Dexie migration, and a per-tap write here would put a third row
 * inside NFR-1's budget.
 *
 * @throws {StorageError} outside a transaction, or on a write failure.
 */
export async function writeUiSession(row: Omit<UiSessionRow, 'id'>): Promise<void> {
  requireTransaction('session.writeUiSession');
  try {
    await db().uiSession.put({ id: SINGLETON_ID, ...row });
  } catch (cause) {
    throw new StorageError('Could not save the current screen.', {
      cause,
      context: { store: TABLE.uiSession, surface: row.surface },
    });
  }
}
